import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/*
 * SPDX-FileCopyrightText: 2024 Genome Research Ltd.
 *
 * SPDX-License-Identifier: MIT
 */
import { useState, useEffect } from "react";
import { Accordion as Acc } from "rsuite";
import AccordionHeader from "./AccordionHeader";
import DropdownButtons from "../../general/DropdownButtons";
import ConfirmationModal from "../ConfirmationModal";
import { httpClient, TsDataSource } from "../../services";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartSimple, faTable } from "@fortawesome/free-solid-svg-icons";
import { useHistory } from "react-router-dom";
import { BOARD_ENDPOINTS, BoardObjectTypes } from "../../constants";
// All of these functions will need to be replaced with TsDataSource ones
// When all MRs have been merged into dev
const returnViewInfo = async (viewId) => {
    try {
        const res = await httpClient().get(`/${BOARD_ENDPOINTS.VIEW}`, {
            params: {
                filter: {
                    and_: {
                        id: { eq: { value: viewId } },
                    },
                },
            },
        });
        return res.data.data[0].attributes.title;
    }
    catch (error) {
        console.error(error);
        return [];
    }
};
const returnZoneInfo = async (zoneId) => {
    try {
        const res = await httpClient().get(`/${BOARD_ENDPOINTS.ZONE}`, {
            params: {
                filter: {
                    and_: {
                        id: { eq: { value: zoneId } },
                    },
                },
            },
        });
        return res.data.data.map((item) => ({
            title: item.attributes.title,
            objectType: item.attributes.object_type,
        }));
    }
    catch (error) {
        console.error(error);
        return [];
    }
};
const returnComponentInfo = async (componentId) => {
    try {
        const res = await httpClient().get(`/${BOARD_ENDPOINTS.COMPONENT}`, {
            params: {
                filter: {
                    and_: {
                        id: { eq: { value: componentId } },
                    },
                },
            },
        });
        return res.data.data.map((item) => ({
            title: item.attributes.title,
            componentType: item.attributes.component_type,
        }));
    }
    catch (error) {
        console.error(error);
        return [];
    }
};
const fetchSubItemId = async (id, endpointUrl, filterItem, itemType) => {
    try {
        const res = await httpClient().get(`/${endpointUrl}`, {
            params: {
                filter: {
                    and_: {
                        [`${filterItem}`]: { eq: { value: id } },
                    },
                },
            },
        });
        return res.data.data.map((item) => ({
            id: item.relationships[itemType].data.id,
            order: item.attributes.order,
        }));
    }
    catch (error) {
        console.error(error);
        return [];
    }
};
const useItemData = (ids, fetchFunction) => {
    const [itemData, setItemData] = useState({});
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        let mounted = true;
        const fetchItems = async () => {
            setLoading(true);
            try {
                const results = await Promise.all(ids.map(async (id) => {
                    const data = await fetchFunction(id);
                    return { [id]: data };
                }));
                if (mounted) {
                    setItemData(Object.assign({}, ...results));
                }
            }
            finally {
                if (mounted) {
                    setLoading(false);
                }
            }
        };
        fetchItems();
        return () => {
            mounted = false;
        };
    }, [ids, fetchFunction]);
    return { itemData, loading };
};
function Accordion(props) {
    const { boardDetails, setBoardDetails } = props;
    const history = useHistory();
    const [openDelete, setOpenDelete] = useState(false);
    const [boardIdToDelete, setBoardIdToDelete] = useState(null);
    const goToBoard = (boardId) => {
        history.push(`/board/${boardId}`);
    };
    // @ts-ignore
    const goToView = (boardId, viewId) => {
        history.push(`/board/${boardId}`);
    };
    // @ts-ignore
    const deleteConfirmationModal = () => (_jsx(ConfirmationModal, { setOpen: setOpenDelete, open: openDelete, 
        // @ts-ignore
        onConfirmClick: deleteBoard, itemType: "board" }));
    const deleteBoard = () => {
        if (boardIdToDelete === null)
            return;
        const deletedBoard = boardDetails.filter((board) => board.id !== boardIdToDelete);
        setBoardDetails(deletedBoard);
        const ds = new TsDataSource();
        ds.custom(`${BOARD_ENDPOINTS.DELETE_BOARD}/${boardIdToDelete}`, "DELETE");
        setBoardIdToDelete(null);
    };
    const handleDelete = (id) => {
        setBoardIdToDelete(id);
        setOpenDelete(true);
    };
    const boardOptionsButton = {
        outline: true,
        type: "primary",
        icon: "ellipsis-v",
        className: "my-boards-dropdown-buttons",
    };
    const dropdownButtons = (boardId, viewId) => [
        {
            name: "View",
            action: () => {
                viewId !== undefined ? goToView(boardId, viewId) : goToBoard(boardId);
            },
        },
        {
            name: "Delete",
            action: () => {
                handleDelete(boardId);
            },
        },
    ];
    const boardOptionsDropdownButton = (boardId, viewId) => (_jsx(DropdownButtons, { mainButtonIcon: boardOptionsButton, placement: "leftStart", dropdownButtons: dropdownButtons(boardId, viewId), showMessages: false, menuStyle: {
            position: "absolute",
            zIndex: "1050",
            display: "inline-block",
        } }));
    const AccordionBase = (props) => {
        const { id, itemType, filterItem, endpointUrl, title, subHeader, renderChildren, infoText, clickable, } = props;
        const [childIds, setChildIds] = useState([]);
        const [loading, setLoading] = useState(false);
        const [expanded, setExpanded] = useState(false);
        const handleExpand = async () => {
            if (!expanded) {
                setLoading(true);
                const ids = await fetchSubItemId(id, endpointUrl, filterItem, itemType);
                setChildIds(ids.map((id) => id.id));
                setLoading(false);
                setExpanded(true);
            }
        };
        return (_jsx(Acc, { bordered: true, style: { flex: "1", overflow: "visible" }, onClick: handleExpand, children: _jsx(Acc.Panel, { style: { overflow: "visible", flex: "1" }, header: _jsx("div", { onClick: clickable ? () => goToBoard(id) : undefined, children: _jsx(AccordionHeader, { header: title, subHeader: subHeader, infoText: infoText || `ID: ${id}` }) }), children: loading ? _jsx("div", { children: "Loading..." }) : renderChildren(childIds) }) }));
    };
    const Components = (props) => {
        const { componentIds } = props;
        const { itemData: componentData, loading } = useItemData(componentIds, returnComponentInfo);
        if (!componentIds?.length)
            return null;
        if (loading)
            return _jsx("div", { style: { textAlign: "center" }, children: "Loading views..." });
        const componentTitle = (title, componentType) => {
            return (_jsxs("span", { style: {
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                }, children: [_jsx(FontAwesomeIcon, { icon: componentType === "chart" ? faChartSimple : faTable }), _jsx("p", { style: { marginLeft: "20px" }, children: title })] }));
        };
        return (_jsxs("div", { children: [_jsx("p", { style: { marginBottom: "10px" }, children: "Zone Components:" }), componentIds.map((componentId) => {
                    const component = componentData[componentId][0];
                    return (_jsx("div", { style: { marginTop: "5px" }, children: componentTitle(component.title, component.componentType) }, componentId));
                })] }));
    };
    const ZonesAccordion = (props) => {
        const { zoneIds } = props;
        const { itemData: zoneData, loading } = useItemData(zoneIds, returnZoneInfo);
        if (!zoneIds?.length)
            return null;
        if (loading)
            return _jsx("div", { style: { textAlign: "center" }, children: "Loading views..." });
        return (_jsx("div", { children: zoneIds.map((zoneId) => {
                return (_jsx("div", { style: { marginTop: "15px" }, children: _jsx(AccordionBase, { id: zoneId, endpointUrl: BOARD_ENDPOINTS.ZONE_COMPONENTS, filterItem: "zone.id", itemType: BoardObjectTypes.COMPONENT, title: zoneData[zoneId][0].title || "", subHeader: zoneData[zoneId][0].objectType, clickable: false, renderChildren: (componentIds) => (_jsx(Components, { componentIds: componentIds })) }) }, zoneId));
            }) }));
    };
    const ViewsAccordion = (props) => {
        const { boardId, viewIds } = props;
        const { itemData: viewData, loading } = useItemData(viewIds, returnViewInfo);
        if (!viewIds?.length)
            return null;
        if (loading)
            return _jsx("div", { style: { textAlign: "center" }, children: "Loading views..." });
        return (_jsx("div", { children: viewIds.map((viewId) => {
                return (_jsxs("div", { style: { marginTop: "15px", display: "flex" }, children: [_jsx("div", { style: { flex: "1" }, children: _jsx(AccordionBase, { id: viewId, title: viewData[viewId] || "", endpointUrl: BOARD_ENDPOINTS.VIEW_ZONES, filterItem: "view.id", itemType: BoardObjectTypes.ZONE, clickable: false, renderChildren: (zoneIds) => (_jsx(ZonesAccordion, { zoneIds: zoneIds })) }) }), _jsx("div", { style: {
                                top: "0",
                                marginLeft: "10px",
                                marginRight: "5px",
                                marginTop: "15px",
                            }, children: boardOptionsDropdownButton(boardId, viewId) })] }, viewId));
            }) }));
    };
    return (_jsx("div", { children: boardDetails.map((board) => (_jsxs("div", { style: { marginTop: "20px", display: "flex" }, children: [_jsx("div", { style: { flex: "1" }, children: _jsx(AccordionBase, { id: board.id, title: board.title, endpointUrl: BOARD_ENDPOINTS.BOARD_VIEWS, filterItem: "board.id", itemType: BoardObjectTypes.VIEW, clickable: true, renderChildren: (viewIds) => (_jsx(ViewsAccordion, { boardId: board.id, viewIds: viewIds })) }) }), _jsx("div", { style: {
                        top: "0",
                        marginLeft: "10px",
                        marginRight: "5px",
                        marginTop: "15px",
                    }, children: boardOptionsDropdownButton(board.id, undefined) }), deleteConfirmationModal()] }, board.id))) }));
}
export default Accordion;
